import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * The Leo Club crest, made of light.
 *
 * Tens of thousands of tiny stars, each taking its colour from the crest
 * (/club.png). They are scattered naturally -- denser along edges and lettering
 * so fine detail stays crisp -- and blended additively, so they read as
 * luminous points rather than pixels.
 *
 * Arrival ("star tide"): the stars start as a dim cloud around the crest. A
 * sweep runs from the bottom-left to the top-right of the logo; as it reaches
 * each star, that star flies in along its own curved path (with a short comet
 * tail, fast start, soft landing) and flares as it lands -- so the logo paints
 * itself in one smooth motion, about two seconds end to end.
 *
 * Once formed, the crest is baked into a single image and simply blitted
 * (a few draw calls per frame instead of tens of thousands). It wakes again
 * when the cursor / a finger is near, when you type, on an error, or at
 * sign-in, and goes back to sleep once it has settled.
 *
 * Imperative API (via ref):
 *   pulse(strength)  -- radial ripple, e.g. on a keystroke
 *   shudder()        -- error: jolt + red flash
 *   roar()           -- success: burst outward and fade
 *
 * Honours prefers-reduced-motion: the finished crest is drawn once, still.
 */

// Brightness of each crest star (additive blending, so overlaps add up).
const GAIN = 0.62;

const LEVEL = 255 / 11; // 12 levels per channel: fine enough to keep gradients smooth
function snap(v) { return Math.round(Math.round(v / LEVEL) * LEVEL); }

function makeSprite(rgb, radiusCss, dpr) {
  const r = Math.max(2, Math.ceil(radiusCss * dpr));
  const d = r * 2;
  const c = document.createElement('canvas');
  c.width = d;
  c.height = d;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  const [R, G, B] = rgb;
  grad.addColorStop(0, `rgba(${R},${G},${B},1)`);
  grad.addColorStop(0.22, `rgba(${R},${G},${B},0.8)`);
  grad.addColorStop(0.55, `rgba(${R},${G},${B},0.22)`);
  grad.addColorStop(1, `rgba(${R},${G},${B},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, d, d);
  return c;
}

const LionField = forwardRef(function LionField(_props, ref) {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const api = useRef({});

  useImperativeHandle(ref, () => ({
    pulse: (s) => api.current.pulse && api.current.pulse(s),
    shudder: () => api.current.shudder && api.current.shudder(),
    roar: () => api.current.roar && api.current.roar(),
  }), []);

  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!stage || !canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarse = window.matchMedia('(pointer: coarse)').matches;

    let W = 0, H = 0, dpr = 1, size = 0, cx = 0, cy = 0, half = 1.4;
    let N = 0;
    let X, Y, VX, VY, NX, NY, PH, DL, DUR, OX, OY, CV, AT, ARR, SC, SZ, BK;
    let twinkle = new Uint32Array(0);
    let byBucket = [];
    let palette = [];          // [r,g,b] per bucket
    let sprites = [];
    let areaUnits = 0.5;       // area covered by the crest, in logo-width units squared
    let introEnd = 0;          // ms after start when the last star has landed
    const flares = [];         // queue of stars that just landed (drawn as a brief flash)
    let flareHead = 0;
    let energy = 0;
    let mode = 'idle';
    let modeAt = 0;
    let awake = true;
    let sleepTick = 0;
    let baked = null;
    // Support switch: localStorage leo_fx=full keeps the comet tails even on a slow device.
    let forceFull = false;
    try { forceFull = localStorage.getItem('leo_fx') === 'full'; } catch { /* storage blocked */ }
    let lowPower = false;      // drop the comet tails if the device can't keep up
    let slowFrames = 0;
    let raf = 0;
    let t0 = 0;
    let last = 0;
    let curT = 0;
    let ready = false;
    let disposed = false;
    const ptr = { x: -9999, y: -9999, on: false };

    /* ---------- stars from the crest ---------- */
    function build(img) {
      const wide = window.innerWidth >= 900 && !coarse;
      const SW = wide ? 420 : 320;
      const target = wide ? 20000 : 9000;
      const ar = img.naturalHeight / img.naturalWidth;
      const SH = Math.round(SW * ar);

      const off = document.createElement('canvas');
      off.width = SW;
      off.height = SH;
      const o = off.getContext('2d', { willReadFrequently: true });
      o.drawImage(img, 0, 0, SW, SH);
      const data = o.getImageData(0, 0, SW, SH).data;

      // Pass 1: weight every visible, bright-enough pixel. Edges and lettering
      // (big colour change to a neighbour) get up to 4x the density.
      const weight = new Float32Array(SW * SH);
      let sum = 0;
      let kept = 0;
      for (let y = 0; y < SH; y += 1) {
        for (let x = 0; x < SW; x += 1) {
          const idx = y * SW + x;
          const i = idx * 4;
          if (data[i + 3] < 128) continue;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          // Near-black pixels would vanish under additive light anyway.
          if (0.299 * r + 0.587 * g + 0.114 * b < 26) continue;
          let e = 0;
          if (x + 1 < SW) e += Math.abs(r - data[i + 4]) + Math.abs(g - data[i + 5]) + Math.abs(b - data[i + 6]);
          if (y + 1 < SH) { const j = i + SW * 4; e += Math.abs(r - data[j]) + Math.abs(g - data[j + 1]) + Math.abs(b - data[j + 2]); }
          const w = 1 + 3 * Math.min(1, (e / (255 * 6)) * 5);
          weight[idx] = w;
          sum += w;
          kept += 1;
        }
      }
      areaUnits = Math.max(0.05, (kept / (SW * SH)) * ar);

      // Pass 2: choose the points with error diffusion (Floyd-Steinberg). Each
      // pixel carries the fractional "star owed" to its neighbours, so stars
      // land evenly spread -- blue noise -- instead of clumping the way pure
      // random picks do (clumps read as bright specks). Positions get sub-pixel
      // jitter, so there is still no grid.
      const scale = target / sum;
      const err = new Float32Array(SW * SH);
      const nx = [], ny = [], bk = [];
      const keyToBucket = new Map();
      palette = [];
      for (let y = 0; y < SH; y += 1) {
        const ltr = (y & 1) === 0;              // serpentine scan avoids diagonal streaks
        const dir = ltr ? 1 : -1;
        for (let k = 0; k < SW; k += 1) {
          const x = ltr ? k : SW - 1 - k;
          const idx = y * SW + x;
          const w = weight[idx];
          if (w === 0) continue;
          const v = Math.min(1, w * scale) + err[idx];
          const out = v >= 0.5 ? 1 : 0;
          const e = v - out;
          if (x + dir >= 0 && x + dir < SW) err[idx + dir] += e * 0.4375;
          if (y + 1 < SH) {
            if (x - dir >= 0 && x - dir < SW) err[idx + SW - dir] += e * 0.1875;
            err[idx + SW] += e * 0.3125;
            if (x + dir >= 0 && x + dir < SW) err[idx + SW + dir] += e * 0.0625;
          }
          if (!out) continue;
          const i = idx * 4;
          const r = snap(data[i]), g = snap(data[i + 1]), b = snap(data[i + 2]);
          const key = (r << 16) | (g << 8) | b;
          let bucket = keyToBucket.get(key);
          if (bucket === undefined) {
            bucket = palette.length;
            keyToBucket.set(key, bucket);
            palette.push([r, g, b]);
          }
          nx.push((x + Math.random()) / SW - 0.5);
          ny.push(((y + Math.random()) / SH - 0.5) * ar);
          bk.push(bucket);
        }
      }

      N = nx.length;
      NX = Float32Array.from(nx);
      NY = Float32Array.from(ny);
      BK = Uint16Array.from(bk);
      X = new Float32Array(N);
      Y = new Float32Array(N);
      VX = new Float32Array(N);
      VY = new Float32Array(N);
      PH = new Float32Array(N);
      DL = new Float32Array(N);
      DUR = new Float32Array(N);
      OX = new Float32Array(N);
      OY = new Float32Array(N);
      CV = new Float32Array(N);
      AT = new Float32Array(N);
      ARR = new Uint8Array(N);
      SC = new Float32Array(N);
      SZ = new Float32Array(N);
      byBucket = palette.map(() => []);

      // The sweep runs bottom-left -> top-right across the logo.
      const SX = 0.85, SY = -0.53;
      const tw = [];
      let maxEnd = 0;
      for (let i = 0; i < N; i += 1) {
        byBucket[BK[i]].push(i);
        PH[i] = Math.random() * 6.283;
        SZ[i] = 0.7 + Math.random() * 0.6;        // varied star sizes
        if (Math.random() < 0.09) tw.push(i);      // ~9% twinkle with a soft halo

        // Where the star waits: a broad cloud around the crest (in logo-size units).
        const a = Math.random() * 6.2832;
        const ro = 0.55 + Math.random() * 0.75;
        OX[i] = Math.cos(a) * ro * 1.2;
        OY[i] = Math.sin(a) * ro;
        CV[i] = (Math.random() < 0.5 ? -1 : 1) * (0.14 + Math.random() * 0.26); // how far its path bows

        const key = Math.min(1, Math.max(0, ((nx[i] * SX + ny[i] * SY) + 0.7) / 1.4));
        DL[i] = 120 + key * 650 + Math.random() * 200;
        DUR[i] = 850 + Math.random() * 450;
        if (DL[i] + DUR[i] > maxEnd) maxEnd = DL[i] + DUR[i];
      }
      introEnd = maxEnd + 350;
      twinkle = Uint32Array.from(tw);
    }

    function park() {
      for (let i = 0; i < N; i += 1) {
        ARR[i] = 0;
        SC[i] = 0.5;
        VX[i] = 0;
        VY[i] = 0;
      }
      flares.length = 0;
      flareHead = 0;
      awake = true;
    }

    function settle() {
      for (let i = 0; i < N; i += 1) {
        X[i] = cx + NX[i] * size;
        Y[i] = cy + NY[i] * size;
        VX[i] = 0;
        VY[i] = 0;
        ARR[i] = 1;
        SC[i] = 1;
      }
    }

    function layout() {
      const r = stage.getBoundingClientRect();
      W = Math.max(1, r.width);
      H = Math.max(1, r.height);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const wide = W >= 700;
      // On a phone the crest sits lower and a bit smaller so the brand line at
      // the top of the screen never overlaps it.
      size = wide ? Math.min(W * 0.8, H * 0.72, 640) : Math.min(W * 0.6, H * 0.72);
      cx = W / 2;
      cy = H * (wide ? 0.5 : 0.57);

      // Star radius follows the average spacing between stars, so the crest is
      // evenly filled whatever the screen size or star count.
      const spacing = size * Math.sqrt(areaUnits / Math.max(1, N));
      half = Math.max(1.0, spacing * 1.05);
      sprites = palette.map((c) => makeSprite(c, half * 1.4, dpr));
      baked = null;
    }

    /* The finished crest as one bitmap. */
    function bake() {
      if (!baked) baked = document.createElement('canvas');
      baked.width = canvas.width;
      baked.height = canvas.height;
      const b = baked.getContext('2d');
      b.setTransform(dpr, 0, 0, dpr, 0, 0);
      b.globalCompositeOperation = 'lighter';
      b.globalAlpha = GAIN;
      for (let bi = 0; bi < byBucket.length; bi += 1) {
        const list = byBucket[bi];
        const sp = sprites[bi];
        if (!sp) continue;
        for (let k = 0; k < list.length; k += 1) {
          const i = list[k];
          const hh = half * SZ[i];
          b.drawImage(sp, X[i] - hh * 1.4, Y[i] - hh * 1.4, hh * 2.8, hh * 2.8);
        }
      }
    }

    /* Position of star i at time tt while it is still on its way in. */
    let px = 0, py = 0, pe = 0, pu = 0;
    function flightPos(i, tt) {
      const u = (tt - DL[i]) / DUR[i];
      pu = u;
      const hx = cx + NX[i] * size;
      const hy = cy + NY[i] * size;
      const sx = cx + OX[i] * size;
      const sy = cy + OY[i] * size;
      if (u <= 0) {
        // waiting: a dim star that drifts a little where it is
        px = sx + Math.sin(tt * 0.0011 + PH[i]) * 4;
        py = sy + Math.cos(tt * 0.0009 + PH[i]) * 4;
        pe = 0;
        return;
      }
      const uu = u > 1 ? 1 : u;
      // fast start, long soft landing (smooth-stepped ease-out)
      const w = uu * 6 > 1 ? 1 : uu * 6;
      const e = (1 - Math.pow(1 - uu, 3.2)) * (w * w * (3 - 2 * w));
      pe = e;
      // quadratic Bezier bowed sideways by this star's own curvature
      const dx = hx - sx, dy = hy - sy;
      const len = Math.hypot(dx, dy) || 1;
      const cxp = (sx + hx) / 2 + (-dy / len) * len * CV[i];
      const cyp = (sy + hy) / 2 + (dx / len) * len * CV[i];
      const m = 1 - e;
      px = m * m * sx + 2 * m * e * cxp + e * e * hx;
      py = m * m * sy + 2 * m * e * cyp + e * e * hy;
    }

    function wake() { awake = true; sleepTick = 0; }

    function isSettled() {
      let dev = 0, vel = 0;
      for (let i = 0; i < N; i += 1) {
        const d = Math.abs(X[i] - (cx + NX[i] * size)) + Math.abs(Y[i] - (cy + NY[i] * size));
        if (d > dev) dev = d;
        const v = Math.abs(VX[i]) + Math.abs(VY[i]);
        if (v > vel) vel = v;
      }
      return dev < 0.35 && vel < 0.04 && energy < 0.03;
    }

    /* ---------- frame ---------- */
    function frame(now) {
      raf = requestAnimationFrame(frame);
      if (!ready) return;
      const dtRaw = (now - last) / 16.667 || 1;
      const dt = Math.min(2.5, dtRaw);
      last = now;
      // The app shows a ~1.2 s start-up splash over the login screen. Hold the
      // intro's clock until it has gone, so the whole arrival is seen.
      const splash = document.getElementById('leo-preloader-overlay');
      if (splash && !splash.classList.contains('leo-hide')) t0 = now;
      const t = now - t0;
      curT = t;

      // Can't hold ~30 fps during the arrival? Drop the comet tails.
      if (!forceFull && !lowPower && t < introEnd && t > 300) {
        slowFrames = dtRaw > 2 ? slowFrames + 1 : Math.max(0, slowFrames - 1);
        if (slowFrames > 12) lowPower = true;
      }

      const near = ptr.on && Math.hypot(ptr.x - cx, ptr.y - cy) < size * 0.62;
      if (!awake && (near || mode === 'roar')) wake();

      let alpha = 1;
      if (mode === 'roar') alpha = Math.max(0, 1 - (now - modeAt) / 950);

      if (awake) {
        const damp = Math.pow(0.86, dt);
        energy *= Math.pow(0.94, dt);
        const amp = energy * 2.2;          // no idle wobble: the sleeping crest is a still image
        const K = 0.05;
        const R = Math.max(70, size * 0.16);
        const R2 = R * R;

        for (let i = 0; i < N; i += 1) {
          if (mode === 'roar') {
            VX[i] *= Math.pow(0.985, dt);
            VY[i] *= Math.pow(0.985, dt);
          } else {
            if (!ARR[i]) {
              flightPos(i, t);
              if (pu < 1) {
                X[i] = px;
                Y[i] = py;
                SC[i] = 0.5 + 0.5 * pe;
                continue;
              }
              ARR[i] = 1;
              SC[i] = 1;
              AT[i] = t;
              flares.push(i);
              X[i] = cx + NX[i] * size;
              Y[i] = cy + NY[i] * size;
              VX[i] = 0;
              VY[i] = 0;
            }
            const tx = cx + NX[i] * size + (amp > 0.05 ? Math.sin(now * 0.0012 + PH[i]) * amp : 0);
            const ty = cy + NY[i] * size + (amp > 0.05 ? Math.cos(now * 0.001 + PH[i] * 1.7) * amp : 0);
            let ax = (tx - X[i]) * K;
            let ay = (ty - Y[i]) * K;
            if (ptr.on) {
              const dx = X[i] - ptr.x;
              const dy = Y[i] - ptr.y;
              const d2 = dx * dx + dy * dy;
              if (d2 < R2 && d2 > 0.01) {
                const d = Math.sqrt(d2);
                const f = (1 - d / R) * 2.6;
                ax += (dx / d) * f;
                ay += (dy / d) * f;
              }
            }
            VX[i] = (VX[i] + ax * dt) * damp;
            VY[i] = (VY[i] + ay * dt) * damp;
          }
          X[i] += VX[i] * dt;
          Y[i] += VY[i] * dt;
        }

        // Settled and untouched? Freeze the crest into one bitmap.
        if (mode !== 'roar' && !near && t > introEnd) {
          sleepTick += 1;
          if (sleepTick % 15 === 0 && isSettled()) {
            settle();
            bake();
            awake = false;
          }
        } else {
          sleepTick = 0;
        }
      }
      draw(now, alpha);
    }

    function draw(now, alpha) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, W, H);
      const intro = Math.min(1, Math.max(0, (now - t0) / 500));

      // Everything is light: overlapping stars add up instead of covering.
      ctx.globalCompositeOperation = 'lighter';

      // soft light that follows the cursor
      if (ptr.on && !coarse && alpha > 0.2) {
        const g = ctx.createRadialGradient(ptr.x, ptr.y, 0, ptr.x, ptr.y, 170);
        g.addColorStop(0, 'rgba(160,190,255,0.12)');
        g.addColorStop(1, 'rgba(160,190,255,0)');
        ctx.globalAlpha = 1;
        ctx.fillStyle = g;
        ctx.fillRect(ptr.x - 170, ptr.y - 170, 340, 340);
      }

      if (awake || !baked) {
        // the crest's stars, grouped by colour
        for (let b = 0; b < byBucket.length; b += 1) {
          const list = byBucket[b];
          const sp = sprites[b];
          if (!sp || list.length === 0) continue;
          ctx.globalAlpha = alpha * intro * GAIN;
          for (let k = 0; k < list.length; k += 1) {
            const i = list[k];
            const hh = half * SC[i] * SZ[i];
            ctx.drawImage(sp, X[i] - hh * 1.4, Y[i] - hh * 1.4, hh * 2.8, hh * 2.8);
          }
        }

        // comet tails on stars that are flying in
        if (!lowPower && mode !== 'roar' && curT < introEnd) {
          for (let i = 0; i < N; i += 3) {
            if (ARR[i]) continue;
            flightPos(i, curT);
            if (pu <= 0 || pu >= 1) continue;
            const sp = sprites[BK[i]];
            const hh = half * SC[i] * SZ[i];
            flightPos(i, curT - 45);
            ctx.globalAlpha = alpha * intro * GAIN * 0.55;
            ctx.drawImage(sp, px - hh * 1.3, py - hh * 1.3, hh * 2.6, hh * 2.6);
            flightPos(i, curT - 95);
            ctx.globalAlpha = alpha * intro * GAIN * 0.25;
            ctx.drawImage(sp, px - hh * 1.1, py - hh * 1.1, hh * 2.2, hh * 2.2);
          }
        }
      } else {
        // asleep: one bitmap, with a very slow breath
        const s = 1 + 0.0035 * Math.sin(now * 0.0011);
        ctx.globalAlpha = 1;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(s, s);
        ctx.translate(-cx, -cy);
        ctx.drawImage(baked, 0, 0, W, H);
        ctx.restore();
      }

      // flare: a star flashes as it lands
      const FLARE_MS = 320;
      while (flareHead < flares.length && curT - AT[flares[flareHead]] > FLARE_MS) flareHead += 1;
      if (flareHead > 4096) { flares.splice(0, flareHead); flareHead = 0; }
      for (let k = flareHead; k < flares.length; k += 1) {
        const i = flares[k];
        const age = (curT - AT[i]) / FLARE_MS;
        const f = 1 - age;
        ctx.globalAlpha = alpha * 0.55 * f * f;
        const hh = half * (2.2 + 1.4 * age);
        ctx.drawImage(sprites[BK[i]], X[i] - hh, Y[i] - hh, hh * 2, hh * 2);
      }

      // a scattering of stars twinkle and glow
      if (!lowPower || !awake) {
        for (let k = 0; k < twinkle.length; k += 1) {
          const i = twinkle[k];
          if (!ARR[i]) continue;
          const tw = 0.5 + 0.5 * Math.sin(now * 0.0021 + PH[i] * 3);
          ctx.globalAlpha = alpha * intro * 0.2 * tw;
          const hh = half * 3.4;
          ctx.drawImage(sprites[BK[i]], X[i] - hh, Y[i] - hh, hh * 2, hh * 2);
        }
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    /* ---------- imperative API ---------- */
    api.current.pulse = (s = 1) => {
      if (reduce || !ready || mode === 'roar') return;
      wake();
      energy = Math.min(4, energy + 0.5 * s);
      const kick = 0.9 * s * (size / 520);
      for (let i = 0; i < N; i += 1) {
        const dx = X[i] - cx;
        const dy = Y[i] - cy;
        const d = Math.hypot(dx, dy) || 1;
        const f = kick * (0.3 + Math.random() * 0.7);
        VX[i] += (dx / d) * f;
        VY[i] += (dy / d) * f;
      }
    };
    api.current.shudder = () => {
      stage.classList.remove('is-err');
      void stage.offsetWidth; // restart the CSS flash
      stage.classList.add('is-err');
      if (reduce || !ready || mode === 'roar') return;
      wake();
      const k = size / 520;
      for (let i = 0; i < N; i += 1) {
        VX[i] += (Math.random() - 0.5) * 9 * k;
        VY[i] += (Math.random() - 0.5) * 5 * k;
      }
    };
    api.current.roar = () => {
      if (reduce || !ready) return;
      wake();
      mode = 'roar';
      modeAt = performance.now();
      const k = size / 520;
      for (let i = 0; i < N; i += 1) {
        const dx = X[i] - cx;
        const dy = Y[i] - cy;
        const d = Math.hypot(dx, dy) || 1;
        const sp = (3 + Math.random() * 9) * k;
        VX[i] = (dx / d) * sp + (Math.random() - 0.5) * 2;
        VY[i] = (dy / d) * sp + (Math.random() - 0.5) * 2 - 1.2 * k;
      }
    };

    /* ---------- input ---------- */
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      ptr.x = e.clientX - r.left;
      ptr.y = e.clientY - r.top;
      ptr.on = ptr.x > -80 && ptr.x < r.width + 80 && ptr.y > -80 && ptr.y < r.height + 80;
    };
    const onLeave = () => { ptr.on = false; };

    /* ---------- boot ---------- */
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      build(img);
      layout();
      t0 = performance.now();
      last = t0;
      if (reduce) {
        settle();
        bake();
        awake = false;
        ready = true;
        draw(t0 + 5000, 1);
      } else {
        park();
        ready = true;
        raf = requestAnimationFrame(frame);
      }
    };
    img.src = '/club.png';

    const ro = new ResizeObserver(() => {
      if (!ready) return;
      layout();
      if (reduce) { settle(); bake(); awake = false; draw(performance.now(), 1); } else { wake(); }
    });
    ro.observe(stage);

    if (!reduce) {
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onLeave, { passive: true });
      window.addEventListener('pointercancel', onLeave, { passive: true });
      document.addEventListener('pointerleave', onLeave);
    }

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onLeave);
      window.removeEventListener('pointercancel', onLeave);
      document.removeEventListener('pointerleave', onLeave);
      api.current = {};
    };
  }, []);

  return (
    <div className="lion-stage" ref={stageRef} aria-hidden="true">
      <div className="lion-aura" />
      <canvas className="lion-canvas" ref={canvasRef} />
      <div className="lion-flash" />
      <div className="lion-motto">Leadership &middot; Experience &middot; Opportunity</div>
    </div>
  );
});

export default LionField;
